import 'core-js/stable';
import 'regenerator-runtime/runtime';
import 'reflect-metadata';
import '../config';
import { FC, useCallback, useEffect, useState } from 'react';
import { SnackbarProvider } from 'notistack';
import { EmotionCache } from '@emotion/cache';
import ModalProvider from 'mui-modal-provider';
import { AppProps } from 'next/app';
import Script from 'next/script';
import { Box, SxProps, Theme } from '@mui/material';
import createEmotionCache from '../utils/createEmotionCache';
import OGMeta, { OGMetaProps } from '../components/OGMeta';
import { Hydrate, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { usePreserveScroll } from '../utils/hooks/usePreserveScroll';
import { LocationProvider } from '../contexts/locationContext';
import '../stores';
import { GoogleApiContextProvider } from '../contexts/google-api.context';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootThemeProvider } from '../utils/RootThemeProvider';
import { InfoPopupProvider } from '../contexts/info-popup.context';
import { useAppHeight } from '../utils/hooks/useAppHeight';
import { useAnalytics } from '../hooks/use-analytics.hook';
import SmartAppBanner from '../components/ui-v3/smart-app-banner/smart-app-banner.component';
import { useLottieAnimation } from 'hooks/useLottieAnimation.hook';
import { Colors, useAuthCookies, useAutoFocusOnMount } from '@sweatpals/react-ui-kit';
import { LocationPageProps } from './[...params]';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { GOOGLE_API_KEY, isTouch, mainElemId } from '../constants';
import { useRouter } from 'next/router';
import { GoogleReCaptchaProvider } from '../components/ui-v3/re-captcha/google-re-captcha-provider.component';
import './styles.css';
import { authStore } from '../stores';
import { initSweatPalsFBPixel } from '../lib/fb-pixel';
import { sweatPalsFBPixelID } from '../constants/fb-pixel';
import { GoogleTagManager } from '@next/third-parties/google';
import { sweatpalsGTagManagerId } from '../constants/gtm';
import { useClarity } from '../hooks/useClarity.hook';
import { useIntercom } from '../hooks/useIntercom.hook';
import { useInitializeNewRelic } from '../hooks/use-newrelic.hook';

const mainAppStyle = (isEmbed: boolean, enableScroll?: boolean): SxProps<Theme> => ({
	flex: 1,
	overflowY: isEmbed && !enableScroll ? 'hidden' : 'auto',

	'&::-webkit-scrollbar': {
		width: isEmbed ? '7px' : '12px',
	},

	'&::-webkit-scrollbar-thumb': {
		borderRadius: '3px',
		backgroundColor: Colors.black_a30,
	},
});

const clientSideEmotionCache = createEmotionCache();

export interface IAppPageProps extends LocationPageProps {
	dehydratedState?: Record<string, unknown>;
}

export interface ICustomAppProps extends AppProps<IAppPageProps> {
	emotionCache: EmotionCache;
}

const CustomApp: FC<ICustomAppProps> = ({ Component, emotionCache = clientSideEmotionCache, pageProps }) => {
	const router = useRouter();

	const [isEmbedPage, setIsEmbedPage] = useState(false);
	const [isMainPage, setIsMainPage] = useState(false);
	const [isDiscoveryPage, setIsDiscoveryPage] = useState(false);

	const isSmartBannerHidden = isEmbedPage || isMainPage || isDiscoveryPage;

	useEffect(() => {
		if (window.location.pathname.startsWith('/embed')) {
			setIsEmbedPage(true);
		} else {
			setIsEmbedPage(false);
		}

		if (window.location.pathname === '/') {
			setIsMainPage(true);
		} else {
			setIsMainPage(false);
		}

		if (window.location.pathname === '/events') {
			setIsDiscoveryPage(true);
		} else {
			setIsDiscoveryPage(false);
		}
	});

	const onAuthChange = useCallback(async (tokens: { accessToken?: string; refreshToken?: string }) => {
		const changed = authStore.checkTokensChanged(tokens.accessToken ?? '', tokens.refreshToken ?? '');

		if (changed) {
			console.debug('Tokens changed', tokens);

			if (tokens.accessToken && tokens.refreshToken) {
				await authStore.setAuth(tokens.accessToken, tokens.refreshToken);
			} else {
				authStore.logout();
			}

			initSweatPalsFBPixel();
		}
	}, []);

	useAuthCookies({
		domain: process.env['NEXT_PUBLIC_COOKIES_DOMAIN'] ?? '',
		prefix: process.env['NEXT_PUBLIC_COOKIES_PREFIX'],
		onChange: onAuthChange,
		interval: 5_000,
	});

	const [queryClient] = useState(
		() =>
			new QueryClient({
				defaultOptions: {
					queries: {
						refetchOnWindowFocus: false,
					},
				},
			})
	);

	useInitializeNewRelic();

	useAnalytics();

	usePreserveScroll();

	useAppHeight();

	useLottieAnimation();

	useAutoFocusOnMount();

	useClarity();

	useIntercom();

	const seo: OGMetaProps = pageProps.seo ?? {};

	return (
		<>
			{/* <!-- Meta Pixel Code --> */}
			<Script
				id='fb-pixel'
				strategy='beforeInteractive'
				dangerouslySetInnerHTML={{
					__html: `
					!function(f,b,e,v,n,t,s)
					{if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					n.queue=[];t=b.createElement(e);t.async=!0;
					t.src=v;s=b.getElementsByTagName(e)[0];
					s.parentNode.insertBefore(t,s)}(window, document, 'script',
					'https://connect.facebook.net/en_US/fbevents.js');

					if (${!!sweatPalsFBPixelID}) {
						fbq('init', '${sweatPalsFBPixelID}');
						console.debug('fbq.init', '${sweatPalsFBPixelID}');
						fbq('track', 'PageView');
						console.debug('fbq.track', 'PageView');
					}
					`,
				}}
				onReady={() => {
					initSweatPalsFBPixel();
				}}
			/>
			{/* <!-- End Meta Pixel Code --> */}

			{/* <!-- Google Tag Manager --> */}
			{sweatpalsGTagManagerId && <GoogleTagManager gtmId={sweatpalsGTagManagerId} />}
			{/* <!-- End Google Tag Manager --> */}

			<OGMeta
				pageTitle='SweatPals'
				siteName='SweatPals'
				title='Whatever your ability, this is your community'
				description='SweatPals is a home for all your fitness, wellness and mindfulness events, communities and friends'
				image='https://media.sweatpals.com/static_media/default_thumbnail.webp'
				url='https://www.sweatpals.com/'
				twitterCardType='summary_large_image'
				twitterLabel1='Est. reading time'
				twitterData1='13 minutes'
				{...seo}
				hideSmartBanner={isSmartBannerHidden}
			>
				<meta property='article:modified_time' key='article:modified_time' content='2023-02-17T11:43:29+00:00' />
			</OGMeta>

			<GoogleReCaptchaProvider>
				<GoogleApiContextProvider apiKey={GOOGLE_API_KEY}>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
						<DndProvider backend={isTouch ? TouchBackend : HTML5Backend}>
							<RootThemeProvider emotionCache={emotionCache}>
								<QueryClientProvider client={queryClient}>
									<SnackbarProvider maxSnack={3} autoHideDuration={3000} preventDuplicate>
										<ModalProvider>
											<InfoPopupProvider>
												<LocationProvider value={pageProps.locationCityId as string}>
													<SmartAppBanner {...pageProps} hide={isSmartBannerHidden} />

													{/* disable scroll for embeds unless they enable it */}
													<Box id={mainElemId} sx={mainAppStyle(isEmbedPage, router.query['enableScroll'] === 'true')}>
														<Hydrate state={pageProps.dehydratedState}>
															<Component {...pageProps} />
														</Hydrate>
													</Box>
													<ReactQueryDevtools />
												</LocationProvider>
											</InfoPopupProvider>
										</ModalProvider>
									</SnackbarProvider>
								</QueryClientProvider>
							</RootThemeProvider>
						</DndProvider>
					</LocalizationProvider>
				</GoogleApiContextProvider>
			</GoogleReCaptchaProvider>
		</>
	);
};

export default CustomApp;
